import React, { useState, useEffect, useCallback } from 'react';
import {
  Wand2,
  CheckCircle,
  Eye,
  Save,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { feedWatchService } from '../../services/feedWatch';
import type {
  MappingConfig,
  MappingRule,
  MappingMode,
  MappingValidation,
  MappingPreviewItem,
  MappingSuggestion,
  CombineToken,
  LookupEntry,
  Condition,
  FeedType,
  ChannelField,
} from '../../services/feedWatch';

// ── Constants ───────────────────────────────────────────────────────

const MAPPING_MODES: { value: MappingMode; label: string }[] = [
  { value: 'use_field', label: 'Use Field' },
  { value: 'static_value', label: 'Static Value' },
  { value: 'combine', label: 'Combine' },
  { value: 'extract_from', label: 'Extract (Regex)' },
  { value: 'lookup_table', label: 'Lookup Table' },
  { value: 'leave_empty', label: 'Leave Empty' },
];

const CONDITION_OPERATORS = [
  'is_not_empty', 'is_empty', 'equals', 'not_equals',
  'contains', 'not_contains', 'starts_with', 'ends_with',
  'is_greater_than', 'is_less_than',
];

const VALUE_OPERATORS = new Set([
  'equals', 'not_equals', 'contains', 'not_contains',
  'starts_with', 'ends_with', 'is_greater_than', 'is_less_than',
]);

// ── Token editor for combine mode ───────────────────────────────

function TokenEditor({
  tokens,
  detectedFields,
  onChange,
}: {
  tokens: CombineToken[];
  detectedFields: string[];
  onChange: (tokens: CombineToken[]) => void;
}) {
  const addToken = (type: 'field' | 'text') => {
    onChange([...tokens, { type, token_type: type, value: '', sort_order: tokens.length }]);
  };

  const updateToken = (idx: number, value: string) => {
    onChange(tokens.map((t, i) => (i === idx ? { ...t, value } : t)));
  };

  const removeToken = (idx: number) => {
    onChange(tokens.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tokens</Label>
      <div className="flex flex-wrap gap-2">
        {tokens.map((token, idx) => (
          <div key={idx} className="flex items-center gap-1.5 bg-background border rounded-lg px-3 py-2 shadow-sm">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-mono">
              {token.type || token.token_type}
            </Badge>
            {(token.type || token.token_type) === 'field' ? (
              <select
                value={token.value}
                onChange={(e) => updateToken(idx, e.target.value)}
                className="text-sm bg-transparent border-none outline-none max-w-[160px]"
              >
                <option value="">Select...</option>
                {detectedFields.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            ) : (
              <Input
                value={token.value}
                onChange={(e) => updateToken(idx, e.target.value)}
                className="h-7 text-sm w-24 px-1.5 border-0 shadow-none"
                placeholder="text"
              />
            )}
            <button onClick={() => removeToken(idx)} className="text-muted-foreground hover:text-destructive transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => addToken('field')}>
          + Field
        </Button>
        <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => addToken('text')}>
          + Text
        </Button>
      </div>
    </div>
  );
}

// ── Lookup table editor ─────────────────────────────────────────

function LookupEditor({
  entries,
  onChange,
}: {
  entries: LookupEntry[];
  onChange: (entries: LookupEntry[]) => void;
}) {
  const addEntry = () => {
    onChange([...entries, { match_value: '', output_value: '', sort_order: entries.length }]);
  };

  const updateEntry = (idx: number, field: 'match_value' | 'output_value', value: string) => {
    onChange(entries.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };

  const removeEntry = (idx: number) => {
    onChange(entries.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      {entries.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <Input
            value={entry.match_value}
            onChange={(e) => updateEntry(idx, 'match_value', e.target.value)}
            placeholder="Match value"
            className="h-10 text-sm flex-1 rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
          <span className="text-muted-foreground shrink-0">&rarr;</span>
          <Input
            value={entry.output_value}
            onChange={(e) => updateEntry(idx, 'output_value', e.target.value)}
            placeholder="Output value"
            className="h-10 text-sm flex-1 rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
          <button onClick={() => removeEntry(idx)} className="text-muted-foreground hover:text-destructive transition-colors p-1">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={addEntry}>
        + Add Row
      </Button>
    </div>
  );
}

// ── Condition editor ────────────────────────────────────────────

function ConditionEditor({
  conditions,
  detectedFields,
  onChange,
}: {
  conditions: Condition[];
  detectedFields: string[];
  onChange: (conditions: Condition[]) => void;
}) {
  const addCondition = () => {
    onChange([...conditions, { field: '', operator: 'equals', value: '', logic: 'AND', sort_order: conditions.length }]);
  };

  const updateCondition = (idx: number, updates: Partial<Condition>) => {
    onChange(conditions.map((c, i) => (i === idx ? { ...c, ...updates } : c)));
  };

  const removeCondition = (idx: number) => {
    onChange(conditions.filter((_, i) => i !== idx));
  };

  return (
    <div className="border-t pt-4 mt-4 space-y-3">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Conditions</Label>
      {conditions.map((cond, idx) => (
        <div key={idx} className="flex items-center gap-2.5 flex-wrap">
          {idx > 0 ? (
            <select
              value={cond.logic}
              onChange={(e) => updateCondition(idx, { logic: e.target.value as 'AND' | 'OR' })}
              className="text-xs bg-muted rounded-md px-2.5 py-1.5 border-none font-semibold"
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          ) : (
            <span className="text-xs font-semibold text-muted-foreground w-10">IF</span>
          )}
          <select
            value={cond.field}
            onChange={(e) => updateCondition(idx, { field: e.target.value })}
            className="text-sm bg-background border-2 rounded-lg px-3 py-2 min-w-[140px] focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
          >
            <option value="">Field...</option>
            {detectedFields.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <select
            value={cond.operator}
            onChange={(e) => updateCondition(idx, { operator: e.target.value })}
            className="text-sm bg-background border-2 rounded-lg px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
          >
            {CONDITION_OPERATORS.map((op) => (
              <option key={op} value={op}>{op.replace(/_/g, ' ')}</option>
            ))}
          </select>
          {VALUE_OPERATORS.has(cond.operator) && (
            <Input
              value={cond.value || ''}
              onChange={(e) => updateCondition(idx, { value: e.target.value })}
              placeholder="value"
              className="h-10 text-sm w-36 rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          )}
          <button onClick={() => removeCondition(idx)} className="text-muted-foreground hover:text-destructive transition-colors p-1">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" className="rounded-lg" onClick={addCondition}>
        + Condition
      </Button>
    </div>
  );
}

// ── Rule Card ────────────────────────────────────────────────────

interface RuleCardProps {
  rule: MappingRule;
  detectedFields: string[];
  channelFields: ChannelField[];
  requiredFields: string[];
  onChange: (rule: MappingRule) => void;
  onDelete: () => void;
}

function RuleCard({ rule, detectedFields, channelFields, requiredFields, onChange, onDelete }: RuleCardProps) {
  const [showConditions, setShowConditions] = useState(rule.apply_to === 'conditional');
  const [customMode, setCustomMode] = useState(false);
  const isRequired = requiredFields.includes(rule.gs_field);

  const updateField = (updates: Partial<MappingRule>) => {
    onChange({ ...rule, ...updates });
  };

  // Check if the current value is a known channel field
  const isKnownField = channelFields.some((f) => f.name === rule.gs_field);
  const showCustomInput = channelFields.length === 0 || customMode || (!isKnownField && rule.gs_field !== '');

  return (
    <Card className={`p-5 border-2 rounded-xl hover:shadow-md transition-all duration-200 ${isRequired ? 'border-l-4 border-l-destructive' : ''}`}>
      {/* Main row: grid layout */}
      <div className="grid grid-cols-[minmax(180px,220px)_160px_1fr_auto] gap-4 items-center">
        {/* Target field selector */}
        <div className="flex items-center gap-2">
          {isRequired ? (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <div className="h-10 text-sm font-mono rounded-lg border-2 border-muted bg-muted/30 px-3 flex items-center text-foreground cursor-not-allowed select-none flex-1">
                {rule.gs_field}
              </div>
            </div>
          ) : showCustomInput ? (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <Input
                value={rule.gs_field}
                onChange={(e) => updateField({ gs_field: e.target.value })}
                className="h-10 text-sm font-mono rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="target_field"
              />
              {channelFields.length > 0 && (
                <button
                  onClick={() => { setCustomMode(false); updateField({ gs_field: '' }); }}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 shrink-0"
                  title="Switch to dropdown"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ) : (
            <Select
              value={rule.gs_field || '__placeholder__'}
              onValueChange={(v) => {
                if (v === '__custom__') {
                  setCustomMode(true);
                  updateField({ gs_field: '' });
                } else if (v !== '__placeholder__') {
                  updateField({ gs_field: v });
                }
              }}
            >
              <SelectTrigger className="h-10 text-sm font-mono rounded-lg border-2">
                <SelectValue placeholder="Select target field..." />
              </SelectTrigger>
              <SelectContent>
                {channelFields.map((f) => (
                  <SelectItem key={f.name} value={f.name}>
                    {f.name}{f.required ? ' *' : ''}
                  </SelectItem>
                ))}
                <SelectItem value="__custom__">
                  <span className="text-muted-foreground italic">Custom...</span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
          {isRequired && <span className="text-destructive text-sm font-bold shrink-0">*</span>}
        </div>

        {/* Mode selector */}
        <Select value={rule.mode} onValueChange={(v) => updateField({ mode: v as MappingMode })}>
          <SelectTrigger className="h-10 text-sm rounded-lg border-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MAPPING_MODES.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Mode-specific input */}
        <div className="min-w-0">
          {rule.mode === 'use_field' && (
            <select
              value={rule.source_field || ''}
              onChange={(e) => updateField({ source_field: e.target.value })}
              className="text-sm bg-background border-2 rounded-lg px-3 py-2.5 w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            >
              <option value="">Select source field...</option>
              {detectedFields.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          )}

          {rule.mode === 'static_value' && (
            <Input
              value={rule.static_value || ''}
              onChange={(e) => updateField({ static_value: e.target.value })}
              placeholder="Static value"
              className="h-10 text-sm rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          )}

          {rule.mode === 'extract_from' && (
            <div className="flex items-center gap-2">
              <select
                value={rule.source_field || ''}
                onChange={(e) => updateField({ source_field: e.target.value })}
                className="text-sm bg-background border-2 rounded-lg px-3 py-2.5 min-w-[120px] focus:border-primary transition-all"
              >
                <option value="">Source...</option>
                {detectedFields.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <Input
                value={rule.regex_pattern || ''}
                onChange={(e) => updateField({ regex_pattern: e.target.value })}
                placeholder="Regex pattern"
                className="h-10 text-sm flex-1 font-mono rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <Input
                value={rule.default_value || ''}
                onChange={(e) => updateField({ default_value: e.target.value })}
                placeholder="Default"
                className="h-10 text-sm w-28 rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          )}

          {rule.mode === 'leave_empty' && (
            <span className="text-sm text-muted-foreground italic">No value will be set</span>
          )}

          {rule.mode === 'combine' && (
            <span className="text-sm text-muted-foreground italic">Configure tokens below</span>
          )}

          {rule.mode === 'lookup_table' && (
            <select
              value={rule.source_field || ''}
              onChange={(e) => updateField({ source_field: e.target.value })}
              className="text-sm bg-background border-2 rounded-lg px-3 py-2.5 w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            >
              <option value="">Select source field...</option>
              {detectedFields.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            variant={showConditions ? 'secondary' : 'ghost'}
            size="sm"
            className="h-9 text-xs px-3 rounded-lg"
            onClick={() => {
              const next = !showConditions;
              setShowConditions(next);
              updateField({ apply_to: next ? 'conditional' : 'all' });
            }}
          >
            {showConditions ? <ChevronDown className="h-3.5 w-3.5 mr-1" /> : <ChevronRight className="h-3.5 w-3.5 mr-1" />}
            If
          </Button>
          {!isRequired && (
            <button onClick={onDelete} className="text-muted-foreground hover:text-destructive transition-colors p-2 rounded-lg hover:bg-destructive/10">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded content: combine tokens */}
      {rule.mode === 'combine' && (
        <div className="mt-4 bg-muted/30 rounded-xl p-5 border space-y-4">
          <TokenEditor
            tokens={rule.tokens || []}
            detectedFields={detectedFields}
            onChange={(tokens) => updateField({ tokens })}
          />
          <div className="flex items-center gap-3">
            <Label className="text-sm text-muted-foreground">Separator:</Label>
            <Input
              value={rule.separator ?? ' '}
              onChange={(e) => updateField({ separator: e.target.value })}
              className="h-10 text-sm w-24 rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      )}

      {/* Expanded content: lookup table */}
      {rule.mode === 'lookup_table' && (
        <div className="mt-4 bg-muted/30 rounded-xl p-5 border space-y-4">
          <LookupEditor
            entries={rule.lookup_entries || []}
            onChange={(lookup_entries) => updateField({ lookup_entries })}
          />
          <div className="flex items-center gap-3">
            <Label className="text-sm text-muted-foreground">Default:</Label>
            <Input
              value={rule.default_value || ''}
              onChange={(e) => updateField({ default_value: e.target.value })}
              className="h-10 text-sm w-36 rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      )}

      {/* Conditions */}
      {showConditions && (
        <ConditionEditor
          conditions={rule.conditions || []}
          detectedFields={detectedFields}
          onChange={(conditions) => updateField({ conditions })}
        />
      )}
    </Card>
  );
}

// ── Preview Dialog ──────────────────────────────────────────────

interface PreviewDialogProps {
  items: MappingPreviewItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function PreviewDialog({ items, open, onOpenChange }: PreviewDialogProps) {
  const [activeIdx, setActiveIdx] = useState(0);

  if (items.length === 0) return null;

  const item = items[activeIdx];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>Mapping Preview</span>
            <Badge variant="secondary" className="font-normal">
              {items.length} product{items.length !== 1 ? 's' : ''}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Product selector */}
        {items.length > 1 && (
          <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg border">
            {items.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIdx(idx)}
                className={`px-3.5 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeIdx === idx
                    ? 'bg-background text-foreground shadow-sm border'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Product {idx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Side-by-side comparison */}
        <div className="grid grid-cols-2 gap-5 flex-1 min-h-0 overflow-hidden">
          <div className="flex flex-col min-h-0">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-muted-foreground/50" />
              Original
            </div>
            <pre className="bg-muted/50 rounded-xl p-5 overflow-auto text-xs flex-1 min-h-0 leading-relaxed border">
              {JSON.stringify(item.original, null, 2)}
            </pre>
          </div>
          <div className="flex flex-col min-h-0">
            <div className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-2 text-green-600">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              Mapped
            </div>
            <pre className="bg-green-500/5 border border-green-500/20 rounded-xl p-5 overflow-auto text-xs flex-1 min-h-0 leading-relaxed">
              {JSON.stringify(item.mapped, null, 2)}
            </pre>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main MappingBuilder ─────────────────────────────────────────

interface MappingBuilderProps {
  feedId: string;
  detectedFields: string[];
  feedType?: FeedType;
}

export function MappingBuilder({ feedId, detectedFields, feedType }: MappingBuilderProps) {
  const [rules, setRules] = useState<MappingRule[]>([]);
  const [configName, setConfigName] = useState('');
  const [channelFields, setChannelFields] = useState<ChannelField[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [validation, setValidation] = useState<MappingValidation | null>(null);
  const [preview, setPreview] = useState<MappingPreviewItem[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const requiredFields = channelFields.filter((f) => f.required).map((f) => f.name);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);

        // Fetch channel spec and mappings in parallel
        const [config, spec] = await Promise.all([
          feedWatchService.getMappings(feedId),
          feedType
            ? feedWatchService.getChannelSpec(feedType).catch(() => null)
            : Promise.resolve(null),
        ]);

        if (spec) {
          setChannelFields(spec.fields);
        }

        let loadedRules: MappingRule[] = [];

        if (config) {
          // Use existing saved rules
          loadedRules = [...config.rules];
          setConfigName(config.name);
        } else {
          // No saved mapping — auto-suggest to pre-populate
          if (spec) setConfigName(spec.label);
          try {
            const suggestions = await feedWatchService.suggestMappings(feedId);
            loadedRules = suggestions.map((s): MappingRule => ({
              gs_field: s.gs_field,
              mode: 'use_field',
              source_field: s.suggested_source_field,
              apply_to: 'all',
            }));
          } catch {
            // Suggestions failed — continue with empty rules
          }
        }

        // Ensure all required channel fields exist as rules
        if (spec) {
          const mappedFields = new Set(loadedRules.map((r) => r.gs_field));
          const missingRequired = spec.fields
            .filter((f) => f.required && !mappedFields.has(f.name))
            .map((f): MappingRule => ({
              gs_field: f.name,
              mode: 'use_field',
              apply_to: 'all',
            }));
          loadedRules = [...loadedRules, ...missingRequired];
        }

        setRules(loadedRules);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [feedId, feedType]);

  const updateRule = useCallback((index: number, updated: MappingRule) => {
    setRules((prev) => prev.map((r, i) => (i === index ? updated : r)));
  }, []);

  const deleteRule = useCallback((index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addRule = () => {
    // Pre-fill with next unmapped channel field (required first, then optional)
    let nextField = '';
    if (channelFields.length > 0) {
      const mappedFields = new Set(rules.map((r) => r.gs_field));
      const unmappedRequired = channelFields.filter((f) => f.required && !mappedFields.has(f.name));
      const unmappedOptional = channelFields.filter((f) => !f.required && !mappedFields.has(f.name));
      nextField = unmappedRequired[0]?.name || unmappedOptional[0]?.name || '';
    }
    setRules((prev) => [...prev, { gs_field: nextField, mode: 'use_field', apply_to: 'all' }]);
  };

  const handleSuggest = async () => {
    try {
      setIsSuggesting(true);
      setError(null);
      const suggestions = await feedWatchService.suggestMappings(feedId);
      const newRules: MappingRule[] = suggestions.map((s) => ({
        gs_field: s.gs_field,
        mode: 'use_field' as MappingMode,
        source_field: s.suggested_source_field,
        apply_to: 'all' as const,
      }));
      setRules(newRules);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleValidate = async () => {
    try {
      setIsValidating(true);
      setError(null);
      const result = await feedWatchService.validateMappings(feedId);
      setValidation(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsValidating(false);
    }
  };

  const handlePreview = async () => {
    try {
      setIsPreviewing(true);
      setError(null);
      const items = await feedWatchService.previewMappings(feedId);
      setPreview(items);
      setShowPreview(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMsg(null);
      const cleanedRules = rules.map((r) => {
        const cleaned: MappingRule = { ...r };
        if (r.mode === 'combine' && r.tokens) {
          cleaned.tokens = r.tokens.map((t) => ({
            type: t.type || t.token_type || 'text',
            value: t.value,
          }));
        }
        return cleaned;
      });
      await feedWatchService.updateMappings(feedId, { name: configName, rules: cleanedRules });
      setSuccessMsg('Mappings saved successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredRules = searchTerm
    ? rules.filter((r) => r.gs_field.toLowerCase().includes(searchTerm.toLowerCase()))
    : rules;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Sticky toolbar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm -mx-8 px-8 py-4 border-b">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search fields..."
              className="pl-10 h-10 rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
            />
          </div>
          <Button variant="outline" className="rounded-lg" onClick={handleSuggest} disabled={isSuggesting}>
            {isSuggesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
            Auto-Suggest
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={handleValidate} disabled={isValidating}>
            {isValidating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Validate
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={handlePreview} disabled={isPreviewing}>
            {isPreviewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <div className="flex-1" />
          <Button className="rounded-lg shadow-md" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Mappings
          </Button>
        </div>
      </div>

      {/* Status messages */}
      {error && (
        <div className="flex items-center gap-3 text-sm text-destructive bg-destructive/10 rounded-xl px-5 py-4 border border-destructive/20">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 text-sm text-green-600 bg-green-500/10 rounded-xl px-5 py-4 border border-green-500/20">
          <CheckCircle className="h-5 w-5 shrink-0" />
          {successMsg}
        </div>
      )}

      {/* Validation banner */}
      {validation && (
        <Card className="p-5 border-2 rounded-xl space-y-3">
          <div className="flex items-center gap-3">
            {validation.valid ? (
              <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Valid</Badge>
            ) : (
              <Badge variant="destructive">Invalid</Badge>
            )}
            <span className="text-sm text-muted-foreground">
              {validation.errors.length} errors, {validation.warnings.length} warnings
            </span>
          </div>
          {validation.errors.length > 0 && (
            <div className="space-y-2">
              {validation.errors.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <code className="font-mono text-xs bg-destructive/5 px-1.5 py-0.5 rounded">{e.field}</code>
                  <span>{e.message}</span>
                </div>
              ))}
            </div>
          )}
          {validation.warnings.length > 0 && (
            <div className="space-y-2">
              {validation.warnings.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-yellow-600">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <code className="font-mono text-xs bg-yellow-500/5 px-1.5 py-0.5 rounded">{w.field}</code>
                  <span>{w.message}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Rule cards */}
      <div className="space-y-4">
        {filteredRules.map((rule, idx) => {
          const realIndex = searchTerm ? rules.indexOf(rule) : idx;
          return (
            <RuleCard
              key={realIndex}
              rule={rule}
              detectedFields={detectedFields}
              channelFields={channelFields}
              requiredFields={requiredFields}
              onChange={(updated) => updateRule(realIndex, updated)}
              onDelete={() => deleteRule(realIndex)}
            />
          );
        })}
      </div>

      <Button variant="outline" onClick={addRule} className="w-full rounded-xl border-2 border-dashed h-12 hover:border-primary hover:bg-primary/5 transition-all duration-200">
        <Plus className="h-4 w-4 mr-2" />
        Add Mapping Rule
      </Button>

      {/* Preview dialog */}
      <PreviewDialog
        items={preview}
        open={showPreview}
        onOpenChange={setShowPreview}
      />
    </div>
  );
}
